import { SelectionInfo } from '../types/wordTypes';
import { log, logError } from '../../utils/logging';
import { TrackChangesParser } from '../utils/TrackChangesParser';

/* global Word */

/**
 * Checks if Word.js API is available
 */
function isWordAvailable(): boolean {
  return typeof Word !== 'undefined' && typeof Word.run === 'function';
}

interface FullDocumentContext {
  fullText: string;
  paragraphs: Array<{ index: number; text: string }>;
}

export class DocumentReader {
  /**
   * Extracts paragraph XML from Word document package OOXML
   * getOoxml() returns the full document package, we need to extract just the paragraph part
   */
  private static extractParagraphXmlFromPackage(packageXml: string): string {
    try {
      // Parse the package XML
      const parser = new DOMParser();
      const packageDoc = parser.parseFromString(packageXml, 'text/xml');
      
      // Find the document part (usually /word/document.xml)
      // Try multiple selector patterns for different namespace formats
      const documentPart = packageDoc.querySelector('pkg\\:part[pkg\\:name="/word/document.xml"]') ||
                          packageDoc.querySelector('part[name="/word/document.xml"]') ||
                          packageDoc.querySelector('[name="/word/document.xml"]') ||
                          Array.from(packageDoc.querySelectorAll('part, pkg\\:part')).find((part: Element) => {
                            const name = part.getAttribute('name') || 
                                        part.getAttribute('pkg:name') ||
                                        (part as any).getAttributeNS('http://schemas.microsoft.com/office/2006/xmlPackage', 'name');
                            return name === '/word/document.xml';
                          });
      
      if (documentPart) {
        // Get the XML content from the part
        const xmlData = documentPart.querySelector('pkg\\:xmlData') ||
                       documentPart.querySelector('xmlData') ||
                       Array.from(documentPart.children).find((child: Element) => {
                         const localName = child.localName || child.tagName.split(':').pop();
                         return localName === 'xmlData';
                       });
        
        if (xmlData) {
          // Extract the inner XML (the actual document XML)
          const serializer = new XMLSerializer();
          let documentXml = '';
          
          // Get all child nodes
          Array.from(xmlData.childNodes).forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              documentXml += serializer.serializeToString(node);
            }
          });
          
          // If we have document XML, try to extract the content
          if (documentXml) {
            const docParser = new DOMParser();
            const docXmlDoc = docParser.parseFromString(documentXml, 'text/xml');
            
            // Helper function to find elements by local name (works with namespaces)
            const findElementByLocalName = (parent: Element | Document, localName: string): Element | null => {
              const walker = docXmlDoc.createTreeWalker(
                parent,
                NodeFilter.SHOW_ELEMENT,
                null
              );
              
              let node: Node | null;
              while (node = walker.nextNode()) {
                const element = node as Element;
                const elementLocalName = element.localName || element.tagName.split(':').pop();
                if (elementLocalName === localName) {
                  return element;
                }
              }
              return null;
            };
            
            // Helper function to find all elements by local name
            const findAllElementsByLocalName = (parent: Element, localName: string): Element[] => {
              const results: Element[] = [];
              const walker = docXmlDoc.createTreeWalker(
                parent,
                NodeFilter.SHOW_ELEMENT,
                null
              );
              
              let node: Node | null;
              while (node = walker.nextNode()) {
                const element = node as Element;
                const elementLocalName = element.localName || element.tagName.split(':').pop();
                if (elementLocalName === localName) {
                  results.push(element);
                }
              }
              return results;
            };
            
            // Try to find the body first (selections might contain multiple paragraphs)
            const body = findElementByLocalName(docXmlDoc, 'body');
            
            if (body) {
              // If body has only one paragraph, return just that paragraph
              const paragraphs = findAllElementsByLocalName(body, 'p');
              if (paragraphs.length === 1) {
                return serializer.serializeToString(paragraphs[0]);
              } else if (paragraphs.length > 1) {
                // Multiple paragraphs - return the body content
                // For selections spanning multiple paragraphs, we return the body
                return serializer.serializeToString(body);
              }
            }
            
            // Try to find a single paragraph element (w:p)
            const paragraph = findElementByLocalName(docXmlDoc, 'p');
            if (paragraph) {
              return serializer.serializeToString(paragraph);
            }
            
            // Fallback: return the document XML
            return documentXml;
          }
        }
      }
      
      // Fallback: if we can't parse the package, try to find paragraph XML directly using regex
      // Sometimes the package might contain the paragraph XML in a different format
      
      // Try to find body content first (for selections spanning multiple paragraphs)
      const bodyMatch = packageXml.match(/<w:body[^>]*>[\s\S]*?<\/w:body>/);
      if (bodyMatch) {
        const bodyXml = bodyMatch[0];
        // If body contains only one paragraph, extract just that paragraph
        const singleParagraphMatch = bodyXml.match(/<w:p[^>]*>[\s\S]*?<\/w:p>/);
        if (singleParagraphMatch && (bodyXml.match(/<w:p/g) || []).length === 1) {
          return singleParagraphMatch[0];
        }
        // Otherwise return the body
        return bodyXml;
      }
      
      // Try to find paragraph XML directly
      const paragraphMatch = packageXml.match(/<w:p[^>]*>[\s\S]*?<\/w:p>/);
      if (paragraphMatch) {
        return paragraphMatch[0];
      }
      
      // Last resort: return empty string
      const hasXmlData = documentPart ? (documentPart.querySelector('pkg\\:xmlData') ||
                                        documentPart.querySelector('xmlData') ||
                                        Array.from(documentPart.children).find((child: Element) => {
                                          const localName = child.localName || child.tagName.split(':').pop();
                                          return localName === 'xmlData';
                                        })) : null;
      
      console.warn('[DocumentReader] Could not extract paragraph XML from package', {
        packageLength: packageXml.length,
        packagePreview: packageXml.substring(0, 1000),
        hasDocumentPart: !!documentPart,
        hasXmlData: !!hasXmlData
      });
      return '';
    } catch (error) {
      console.error('[DocumentReader] Error extracting paragraph XML from package:', error);
      return '';
    }
  }

  /**
   * Unwrap package XML from Paragraph/Range.getOoxml() to inner &lt;w:p&gt; (or &lt;w:body&gt; when needed).
   * Same as private extractParagraphXmlFromPackage — exposed for RedlineInserter accepted-text alignment.
   */
  static paragraphOrBodyXmlFromOoxmlPackage(packageXml: string): string {
    return DocumentReader.extractParagraphXmlFromPackage(packageXml);
  }

  /**
   * Gets the currently selected text from the document
   */
  static async getSelectedText(): Promise<SelectionInfo> {
    if (!isWordAvailable()) {
      // Return empty selection when Word.js is not available (browser testing)
      return {
        text: '',
        isEmpty: true,
        range: undefined,
        ooxml: undefined,
        hasTrackChanges: false,
      };
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text,ooxml');
        await context.sync();

        const text = selection.text || '';
        const ooxml = (selection as any).ooxml || '';
        const hasTrackChanges = ooxml.includes('<w:ins') || ooxml.includes('<w:del');

        return {
          text: text,
          isEmpty: !text || text.trim().length === 0,
          range: selection,
          ooxml: ooxml,
          hasTrackChanges: hasTrackChanges,
        };
      });
    } catch (error) {
      logError('Failed to get selected text', error);
      throw error;
    }
  }

  /**
   * Gets text from a specific range
   */
  static async getTextFromRange(range: Word.Range): Promise<string> {
    if (!isWordAvailable()) {
      return '';
    }

    try {
      return await Word.run(async (context) => {
        range.load('text');
        await context.sync();
        return range.text || '';
      });
    } catch (error) {
      logError('Failed to get text from range', error);
      throw error;
    }
  }

  /**
   * Gets the current selection range for later use
   */
  static async getSelectionRange(): Promise<Word.Range | null> {
    if (!isWordAvailable()) {
      return null;
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text');
        await context.sync();
        return selection;
      });
    } catch (error) {
      logError('Failed to get selection range', error);
      return null;
    }
  }

  /**
   * Gets the paragraph at the current cursor position
   * This is useful when user clicks but doesn't select text
   */
  static async getParagraphAtCursor(): Promise<SelectionInfo> {
    if (!isWordAvailable()) {
      return {
        text: '',
        isEmpty: true,
        range: undefined,
        ooxml: undefined,
        hasTrackChanges: false,
      };
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        
        // Get the paragraph that contains the selection
        // If selection is empty (just cursor), getFirst() will get the paragraph at cursor
        const paragraph = selection.paragraphs.getFirst();
        paragraph.load('text,ooxml');
        await context.sync();

        const paragraphText = paragraph.text || '';
        const isEmpty = !paragraphText || paragraphText.trim().length === 0;
        const ooxml = (paragraph as any).ooxml || '';
        const hasTrackChanges = ooxml.includes('<w:ins') || ooxml.includes('<w:del');

        return {
          text: paragraphText.trim(),
          isEmpty: isEmpty,
          range: selection,
          ooxml: ooxml,
          hasTrackChanges: hasTrackChanges,
        };
      });
    } catch (error) {
      logError('Failed to get paragraph at cursor', error);
      throw error;
    }
  }

  /**
   * Gets the current selection or paragraph at cursor if no text is selected
   * This enables click-to-select paragraph functionality
   */
  static async getSelectionOrParagraph(): Promise<SelectionInfo> {
    if (!isWordAvailable()) {
      return {
        text: '',
        isEmpty: true,
        range: undefined,
        ooxml: undefined,
        hasTrackChanges: false,
      };
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text');
        await context.sync();

        const selectedText = selection.text || '';
        const hasSelection = selectedText.trim().length > 0;
        
        // Try to get ooxml using getOoxml() method (required for Word Online)
        let selectionOoxml = '';
        try {
          // Use getOoxml() method which returns a result object
          const ooxmlResult = (selection as any).getOoxml();
          await context.sync();
          
          // Access the value property after sync
          const ooxmlValue = ooxmlResult.value;
          
          if (ooxmlValue !== undefined && ooxmlValue !== null && typeof ooxmlValue === 'string' && ooxmlValue.length > 0) {
            // getOoxml() returns the full document package, we need to extract the paragraph XML
            selectionOoxml = this.extractParagraphXmlFromPackage(ooxmlValue);
            console.log('[DocumentReader] Successfully extracted OOXML from selection using getOoxml():', {
              packageLength: ooxmlValue.length,
              paragraphLength: selectionOoxml.length,
              preview: selectionOoxml.substring(0, 200)
            });
          } else {
            console.warn('[DocumentReader] OOXML from getOoxml() is empty or invalid:', {
              value: ooxmlValue,
              type: typeof ooxmlValue,
              length: ooxmlValue?.length || 0
            });
          }
        } catch (e) {
          console.error('[DocumentReader] Could not get OOXML using getOoxml() method:', e);
        }
        
        const selectionHasTrackChanges = selectionOoxml.length > 0 && 
          (selectionOoxml.includes('<w:ins') || selectionOoxml.includes('<w:del'));

        // Determine which paragraph to use (for paragraph index computation)
        let targetParagraph: any;
        if (hasSelection) {
          // Get the paragraph containing the selection
          targetParagraph = selection.paragraphs.getFirst();
          targetParagraph.load('text');
          await context.sync();
        } else {
          // Get the paragraph at cursor
          targetParagraph = selection.paragraphs.getFirst();
          targetParagraph.load('text');
          await context.sync();
        }

        // Compute paragraph index by matching text in body.paragraphs
        let paragraphIndex: number | undefined = undefined;
        try {
          const paragraphs = context.document.body.paragraphs;
          paragraphs.load('items');
          await context.sync();
          
          // Load text for all paragraphs
          for (const para of paragraphs.items) {
            para.load('text');
          }
          await context.sync();
          
          // Normalize function for comparison
          const normalize = (t: string) => (t || '').replace(/\s+/g, ' ').trim();
          const targetText = normalize(targetParagraph.text || '');
          
          // Find matching paragraph index
          for (let i = 0; i < paragraphs.items.length; i++) {
            if (normalize(paragraphs.items[i].text || '') === targetText) {
              paragraphIndex = i;
              break;
            }
          }
          
          console.log('[DocumentReader] Computed paragraph index:', paragraphIndex, 'for text:', targetText.substring(0, 50));
        } catch (e) {
          console.warn('[DocumentReader] Could not compute paragraph index:', e);
        }

        // If there's text selected, return it
        if (hasSelection) {
          return {
            text: selectedText,
            isEmpty: false,
            range: selection,
            ooxml: selectionOoxml,
            hasTrackChanges: selectionHasTrackChanges,
            paragraphIndex: paragraphIndex,
          };
        }

        // Otherwise, return paragraph at cursor – select its entire text so the user sees it selected
        try {
          (targetParagraph as any).getRange('Whole');
          //paraRange.select();
          await context.sync();
        } catch (e) {
          console.warn('[DocumentReader] Could not select paragraph at cursor:', e);
        }

        const paragraphText = targetParagraph.text || '';
        const isEmpty = !paragraphText || paragraphText.trim().length === 0;
        
        // Try to get ooxml using getOoxml() method (required for Word Online)
        let paragraphOoxml = '';
        try {
          // Use getOoxml() method which returns a result object
          const ooxmlResult = (targetParagraph as any).getOoxml();
          await context.sync();
          
          // Access the value property after sync
          const ooxmlValue = ooxmlResult.value;
          
          if (ooxmlValue !== undefined && ooxmlValue !== null && typeof ooxmlValue === 'string' && ooxmlValue.length > 0) {
            // getOoxml() returns the full document package, we need to extract the paragraph XML
            paragraphOoxml = this.extractParagraphXmlFromPackage(ooxmlValue);
            console.log('[DocumentReader] Successfully extracted OOXML from paragraph using getOoxml():', {
              packageLength: ooxmlValue.length,
              paragraphLength: paragraphOoxml.length,
              preview: paragraphOoxml.substring(0, 200)
            });
          } else {
            console.warn('[DocumentReader] Paragraph OOXML from getOoxml() is empty or invalid:', {
              value: ooxmlValue,
              type: typeof ooxmlValue,
              length: ooxmlValue?.length || 0
            });
          }
        } catch (e) {
          console.error('[DocumentReader] Could not get paragraph OOXML using getOoxml() method:', e);
        }
        
        const paragraphHasTrackChanges = paragraphOoxml.length > 0 && 
          (paragraphOoxml.includes('<w:ins') || paragraphOoxml.includes('<w:del'));

        return {
          text: paragraphText.trim(),
          isEmpty: isEmpty,
          range: targetParagraph,
          ooxml: paragraphOoxml,
          hasTrackChanges: paragraphHasTrackChanges,
          paragraphIndex: paragraphIndex,
        };
      });
    } catch (error) {
      logError('Failed to get selection or paragraph', error);
      throw error;
    }
  }

  /**
   * Paragraph text reads are batched: queue `load('text')` then one `sync()` per chunk.
   * Avoids per-paragraph sync (very slow on large docs).
   */
  private static readonly PARAGRAPH_TEXT_LOAD_BATCH_SIZE = 150;

  private static readonly WORDPROCESSING_NS =
    'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

  private static readonly LIST_NUMBER_PREFIX_RE = /^(\d+(?:\.\d+)*)([.)])?\s+/;

  /**
   * Builds accepted ("Accept All Changes") text per paragraph from body package OOXML.
   * Returns null if package parsing fails.
   */
  private static buildAcceptedParagraphTextsFromPackageXml(
    packageXml: string
  ): { texts: string[]; skippedDeletedOnlyParagraphs: number } | null {
    if (!packageXml || !packageXml.trim()) {
      return null;
    }
    try {
      const bodyXml = DocumentReader.extractParagraphXmlFromPackage(packageXml);
      if (!bodyXml) {
        return null;
      }
      const domParser = new DOMParser();
      const xmlDoc = domParser.parseFromString(bodyXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) {
        return null;
      }
      const NS_W = DocumentReader.WORDPROCESSING_NS;
      const serializer = new XMLSerializer();
      const allPEls = Array.from(xmlDoc.getElementsByTagNameNS(NS_W, 'p'));
      const parts: string[] = [];
      let skippedDeletedOnlyParagraphs = 0;
      for (let i = 0; i < allPEls.length; i++) {
        const pEl = allPEls[i];
        let pXml = serializer.serializeToString(pEl);
        if (!pXml.includes('xmlns:w=')) {
          pXml = pXml.replace(/^(<[^>]+)>/, `$1 xmlns:w="${NS_W}">`);
        }
        const acceptedText = TrackChangesParser.extractAcceptedText(pXml);
        const acceptedTrimmed = acceptedText.trim();
        const originalTrimmed = TrackChangesParser.extractOriginalText(pXml).trim();

        // After accept-all, deleted-only paragraphs have empty accepted text but Office.js
        // still exposes them as body paragraphs. Skipping them broke index alignment with
        // paragraphs.items — keep an empty slot instead.
        if (!acceptedTrimmed && originalTrimmed) {
          skippedDeletedOnlyParagraphs += 1;
        }

        parts.push(acceptedText);
      }
      return { texts: parts, skippedDeletedOnlyParagraphs };
    } catch {
      return null;
    }
  }

  /**
   * Builds original ("Reject All Changes") text per paragraph from body package OOXML.
   * Symmetric to buildAcceptedParagraphTextsFromPackageXml, but uses extractOriginalText.
   * Skips insertion-only paragraphs (those that vanish after reject-all).
   * Returns null if package parsing fails.
   */
  private static buildOriginalParagraphTextsFromPackageXml(
    packageXml: string
  ): { texts: string[]; skippedInsertionOnlyParagraphs: number } | null {
    if (!packageXml || !packageXml.trim()) {
      return null;
    }
    try {
      const bodyXml = DocumentReader.extractParagraphXmlFromPackage(packageXml);
      if (!bodyXml) {
        return null;
      }
      const domParser = new DOMParser();
      const xmlDoc = domParser.parseFromString(bodyXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) {
        return null;
      }
      const NS_W = DocumentReader.WORDPROCESSING_NS;
      const serializer = new XMLSerializer();
      const allPEls = Array.from(xmlDoc.getElementsByTagNameNS(NS_W, 'p'));
      const parts: string[] = [];
      let skippedInsertionOnlyParagraphs = 0;
      for (let i = 0; i < allPEls.length; i++) {
        const pEl = allPEls[i];
        let pXml = serializer.serializeToString(pEl);
        if (!pXml.includes('xmlns:w=')) {
          pXml = pXml.replace(/^(<[^>]+)>/, `$1 xmlns:w="${NS_W}">`);
        }
        const originalText = TrackChangesParser.extractOriginalText(pXml);
        const originalTrimmed = originalText.trim();
        const acceptedTrimmed = TrackChangesParser.extractAcceptedText(pXml).trim();

        // Paragraph exists only as an insertion — it disappears after reject-all.
        if (!originalTrimmed && acceptedTrimmed) {
          skippedInsertionOnlyParagraphs += 1;
          continue;
        }

        parts.push(originalText);
      }
      return { texts: parts, skippedInsertionOnlyParagraphs };
    } catch {
      return null;
    }
  }

  /**
   * Builds per-paragraph text using Office.js paragraphs + listItem.listString.
   * Keeps Word numbering in-context while preserving accepted-text semantics.
   */
  private static async getParagraphTextsWithOfficeNumbering(
    context: Word.RequestContext
  ): Promise<string[]> {
    const startedAt = Date.now();
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    const bodyOoxmlResult = (body as any).getOoxml();
    paragraphs.load('items');
    await context.sync();
    const officeLoadMs = Date.now() - startedAt;

    const n = paragraphs.items.length;
    const batch = DocumentReader.PARAGRAPH_TEXT_LOAD_BATCH_SIZE;
    const plainTexts: string[] = new Array(n).fill('');
    const listStrings: Array<string | undefined> = new Array(n).fill(undefined);

    const acceptedExtractStart = Date.now();
    const packageXml: string = bodyOoxmlResult.value ?? '';
    const acceptedResult = DocumentReader.buildAcceptedParagraphTextsFromPackageXml(packageXml);
    const acceptedTextsRaw = acceptedResult?.texts ?? null;
    let acceptedTexts = acceptedTextsRaw ? [...acceptedTextsRaw] : null;
    let trimmedTrailingEmptyAccepted = 0;
    if (acceptedTexts) {
      while (acceptedTexts.length > n) {
        const last = acceptedTexts[acceptedTexts.length - 1];
        if ((last || '').trim().length > 0) break;
        acceptedTexts.pop();
        trimmedTrailingEmptyAccepted += 1;
      }
    }
    const canUseAcceptedByIndex = !!acceptedTexts && acceptedTexts.length === n;
    const acceptedExtractMs = Date.now() - acceptedExtractStart;

    for (let start = 0; start < n; start += batch) {
      const end = Math.min(start + batch, n);

      for (let i = start; i < end; i++) {
        const para = paragraphs.items[i] as any;
        para.load('text');
        try {
          const li = para.listItemOrNullObject;
          if (li) {
            li.load('listString,isNullObject');
          }
        } catch {
          // listItemOrNullObject/listString may be unavailable in this host
        }
      }

      await context.sync();

      for (let i = start; i < end; i++) {
        const para = paragraphs.items[i] as any;
        const acceptedText = acceptedTexts?.[i] || '';
        const officeText = typeof para.text === 'string' ? para.text : '';
        const paragraphText = acceptedText.trim() ? acceptedText : officeText;
        plainTexts[i] = paragraphText;

        try {
          const li = para.listItemOrNullObject;
          if (li && !li.isNullObject && li.listString != null && String(li.listString).trim() !== '') {
            listStrings[i] = String(li.listString).trim();
          }
        } catch {
          // ignore
        }
      }
    }

    if (!canUseAcceptedByIndex) {
      log('[Perf][ContextBuild] validation_failed', {
        officeParagraphs: n,
        acceptedParagraphs: acceptedTexts?.length ?? 0,
        acceptedParagraphsRaw: acceptedTextsRaw?.length ?? 0,
        trimmedTrailingEmptyAccepted,
        skippedDeletedOnlyParagraphs: acceptedResult?.skippedDeletedOnlyParagraphs ?? 0,
        officeLoadMs,
        acceptedExtractMs,
        totalMs: Date.now() - startedAt,
      });

      throw new Error(
        `Accept-all paragraph validation failed: officeParagraphs=${n}, acceptedParagraphs=${acceptedTexts?.length ?? 0}`
      );
    }

    const out: string[] = new Array(n).fill('');
    for (let i = 0; i < n; i++) {
      const cleanText = (plainTexts[i] || '').trim();
      if (!cleanText) continue;
      const listString = listStrings[i];
      if (listString) {
        const textWithoutLeadingList = cleanText.replace(DocumentReader.LIST_NUMBER_PREFIX_RE, '');
        out[i] = `${listString} ${textWithoutLeadingList}`.trim();
      } else {
        out[i] = cleanText;
      }
    }

    log('[Perf][ContextBuild] success', {
      officeParagraphs: n,
      acceptedParagraphsRaw: acceptedTextsRaw?.length ?? 0,
      acceptedParagraphsAligned: acceptedTexts?.length ?? 0,
      trimmedTrailingEmptyAccepted,
      skippedDeletedOnlyParagraphs: acceptedResult?.skippedDeletedOnlyParagraphs ?? 0,
      nonEmptyOutputParagraphs: out.filter((t) => (t || '').trim().length > 0).length,
      officeTextFallbackParagraphs: plainTexts.filter((text, i) => {
        const acceptedText = acceptedTexts?.[i] || '';
        return !acceptedText.trim() && (text || '').trim().length > 0;
      }).length,
      officeLoadMs,
      acceptedExtractMs,
      totalMs: Date.now() - startedAt,
    });

    return out;
  }

  /**
   * Shared full-document context builder used by Ask and Review.
   */
  static async getFullDocumentContext(): Promise<FullDocumentContext> {
    if (!isWordAvailable()) {
      return { fullText: '', paragraphs: [] };
    }

    return Word.run(async (context) => {
      const startedAt = Date.now();
      const numberedParagraphTexts = await DocumentReader.getParagraphTextsWithOfficeNumbering(context);
      const paragraphs = numberedParagraphTexts
        .map((text, index) => ({ index, text: text.trim() }))
        .filter((p) => p.text.length > 0);
      log('[Perf][ContextBuild] getFullDocumentContext_done', {
        fullTextLength: paragraphs.map((p) => p.text).join('\n').length,
        paragraphCount: paragraphs.length,
        totalMs: Date.now() - startedAt,
      });
      return {
        fullText: paragraphs.map((p) => p.text).join('\n'),
        paragraphs,
      };
    });
  }

  /**
   * Builds document context with "Reject All Changes" semantics — insertions removed,
   * deletions kept. This is the clean/original loan agreement text before client redlines.
   *
   * Returns the same { fullText, paragraphs } shape as getFullDocumentContext(), so it
   * can be sent as structured JSON to the backend (same transport as Proofread / Agent).
   * Unlike getFullDocumentContext(), no paragraph-count alignment with Office.js is required
   * because this context is not used for write-back paragraph targeting.
   */
  static async getFullDocumentContextOriginal(): Promise<FullDocumentContext> {
    if (!isWordAvailable()) {
      return { fullText: '', paragraphs: [] };
    }

    return Word.run(async (context) => {
      const startedAt = Date.now();
      const body = context.document.body;
      const bodyOoxmlResult = (body as any).getOoxml();
      await context.sync();

      const packageXml: string = bodyOoxmlResult.value ?? '';
      const result = DocumentReader.buildOriginalParagraphTextsFromPackageXml(packageXml);

      if (!result) {
        log('[Perf][ContextBuild] getFullDocumentContextOriginal: OOXML parse failed, falling back to empty');
        return { fullText: '', paragraphs: [] };
      }

      const paragraphs = result.texts
        .map((text, index) => ({ index, text: text.trim() }))
        .filter((p) => p.text.length > 0);

      log('[Perf][ContextBuild] getFullDocumentContextOriginal_done', {
        fullTextLength: paragraphs.map((p) => p.text).join('\n').length,
        paragraphCount: paragraphs.length,
        skippedInsertionOnlyParagraphs: result.skippedInsertionOnlyParagraphs,
        totalMs: Date.now() - startedAt,
      });

      return {
        fullText: paragraphs.map((p) => p.text).join('\n'),
        paragraphs,
      };
    });
  }

  /**
   * Gets the full text of the entire document as if all track changes were accepted
   * (insertions kept, deletions omitted — matches "Accept All Changes" for context).
   * Uses body OOXML + TrackChangesParser.extractAcceptedText per paragraph.
   */
  static async getFullDocumentText(): Promise<string> {
    if (!isWordAvailable()) {
      return '';
    }

    try {
      const ctx = await DocumentReader.getFullDocumentContext();
      return ctx.fullText;
    } catch (error) {
      logError('Failed to get full document text', error);
      throw error;
    }
  }

  /**
   * Gets the text from the first page of the document
   * Approximates first page by getting first ~5000 characters or first 20 paragraphs
   * Useful for initial entity extraction without processing entire document
   */
  static async getFirstPageText(): Promise<string> {
    if (!isWordAvailable()) {
      return '';
    }

    try {
      return await Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const maxParagraphs = 20;
        const maxChars = 5000;
        const count = Math.min(paragraphs.items.length, maxParagraphs);

        for (let i = 0; i < count; i++) {
          paragraphs.items[i].load('text');
        }
        await context.sync();

        let firstPageText = '';
        for (let i = 0; i < count; i++) {
          const paragraphText = paragraphs.items[i].text + '\n';
          if (firstPageText.length + paragraphText.length > maxChars) {
            const remaining = maxChars - firstPageText.length;
            if (remaining > 100) {
              firstPageText += paragraphText.substring(0, remaining);
            }
            break;
          }
          firstPageText += paragraphText;
        }

        return firstPageText.trim();
      });
    } catch (error) {
      logError('Failed to get first page text', error);
      throw error;
    }
  }

  /**
   * Gets the full OOXML of the entire document
   * Useful for extracting track changes across the entire document
   */
  static async getFullDocumentOOXML(): Promise<string> {
    if (!isWordAvailable()) {
      return '';
    }

    try {
      return await Word.run(async (context) => {
        const body = context.document.body;
        
        // Try to get ooxml using getOoxml() method
        try {
          const ooxmlResult = (body as any).getOoxml();
          await context.sync();
          
          const ooxmlValue = ooxmlResult.value;
          
          if (ooxmlValue !== undefined && ooxmlValue !== null && typeof ooxmlValue === 'string' && ooxmlValue.length > 0) {
            // getOoxml() returns the full document package, extract the body XML
            const bodyXml = this.extractParagraphXmlFromPackage(ooxmlValue);
            console.log('[DocumentReader] Successfully extracted full document OOXML:', {
              packageLength: ooxmlValue.length,
              bodyLength: bodyXml.length
            });
            return bodyXml;
          } else {
            console.warn('[DocumentReader] Full document OOXML is empty or invalid');
            return '';
          }
        } catch (e) {
          console.error('[DocumentReader] Could not get full document OOXML:', e);
          return '';
        }
      });
    } catch (error) {
      logError('Failed to get full document OOXML', error);
      throw error;
    }
  }

  /**
   * Gets all paragraphs with their indices
   */
  static async getParagraphsWithIndices(): Promise<Array<{index: number, text: string}>> {
    try {
      const ctx = await DocumentReader.getFullDocumentContext();
      return ctx.paragraphs;
    } catch (error) {
      logError('Failed to get paragraphs with indices', error);
      throw error;
    }
  }

  /**
   * Extracts all track-change paragraphs from the live Word document using Office.js.
   *
   * Uses body.getOoxml() (not body.load('ooxml') which is NOT a valid loadable property
   * on Body objects).  getOoxml() returns a package XML wrapper; we unwrap it with
   * extractParagraphXmlFromPackage to get the actual <w:body> content, then parse all
   * <w:p> elements — including those nested inside table cells.
   *
   * Returns:
   *  allParagraphTexts  — plain text of every <w:p> in document order (body + tables).
   *  paragraphsWithTrackChanges — enriched data for paragraphs that contain w:ins or w:del.
   */
  static async extractTrackChangedParagraphs(): Promise<{
    allParagraphTexts: string[];
    paragraphsWithTrackChanges: Array<{
      paragraphIndex: number;
      paragraphXml: string;
      originalText: string;
      editedText: string;
      participants: string[];
    }>;
  }> {
    if (!isWordAvailable()) {
      return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
    }

    try {
      return await Word.run(async (context) => {
        // body.getOoxml() is the correct API — returns a ClientResult<string>.
        // body.load('ooxml') does NOT work on Body objects.
        const ooxmlResult = (context.document.body as any).getOoxml();
        await context.sync();

        const packageXml: string = ooxmlResult.value ?? '';
        if (!packageXml) {
          log('extractTrackChangedParagraphs: body.getOoxml() returned empty');
          return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
        }

        // Unwrap the OPC package to get the <w:body> (or full document.xml) content
        const bodyXml = this.extractParagraphXmlFromPackage(packageXml);
        if (!bodyXml) {
          log('extractTrackChangedParagraphs: extractParagraphXmlFromPackage returned empty');
          return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
        }

        log(`extractTrackChangedParagraphs: packageXml length=${packageXml.length}, bodyXml length=${bodyXml.length}`);

        // ── Parse the body XML (includes table cells) ───────────────────────
        const domParser = new DOMParser();
        const xmlDoc = domParser.parseFromString(bodyXml, 'text/xml');

        if (xmlDoc.querySelector('parsererror')) {
          logError('extractTrackChangedParagraphs: body XML failed to parse');
          return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
        }

        const NS_W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
        const serializer = new XMLSerializer();

        // All <w:p> in document order – body paragraphs AND table-cell paragraphs
        const allPEls = Array.from(xmlDoc.getElementsByTagNameNS(NS_W, 'p'));

        const allParagraphTexts: string[] = [];
        const paragraphsWithTrackChanges: Array<{
          paragraphIndex: number;
          paragraphXml: string;
          originalText: string;
          editedText: string;
          participants: string[];
        }> = [];

        for (let i = 0; i < allPEls.length; i++) {
          const pEl = allPEls[i];

          // ── Plain text: all w:t content, including inside w:ins and w:del ──
          const tEls = Array.from(pEl.getElementsByTagNameNS(NS_W, 't'));
          const text = tEls.map((t) => t.textContent ?? '').join('');
          allParagraphTexts.push(text);

          // ── Track-change detection via DOM ────────────────────────────────
          const insEls = Array.from(pEl.getElementsByTagNameNS(NS_W, 'ins'));
          const delEls = Array.from(pEl.getElementsByTagNameNS(NS_W, 'del'));
          if (insEls.length === 0 && delEls.length === 0) continue;

          // Serialize paragraph XML and ensure w: namespace is declared
          let pXml = serializer.serializeToString(pEl);
          if (!pXml.includes('xmlns:w=')) {
            pXml = pXml.replace(/^(<[^>]+)>/, `$1 xmlns:w="${NS_W}">`);
          }

          // Collect unique authors from w:ins/@w:author and w:del/@w:author
          const getAuthor = (el: Element) =>
            el.getAttributeNS(NS_W, 'author') || el.getAttribute('w:author') || '';
          const participants = [
            ...new Set([...insEls.map(getAuthor), ...delEls.map(getAuthor)].filter(Boolean)),
          ];

          paragraphsWithTrackChanges.push({
            paragraphIndex: i,
            paragraphXml: pXml,
            originalText: TrackChangesParser.extractOriginalText(pXml),
            editedText: TrackChangesParser.extractAcceptedText(pXml),
            participants,
          });
        }

        log(
          `extractTrackChangedParagraphs: ${paragraphsWithTrackChanges.length} TC paragraphs ` +
          `out of ${allParagraphTexts.length} total (body + table cells)`
        );
        return { allParagraphTexts, paragraphsWithTrackChanges };
      });
    } catch (error) {
      logError('Failed to extract track changed paragraphs', error);
      return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
    }
  }

  /**
   * Navigates to a specific paragraph by index
   * Uses direct selection which automatically navigates Word to that location
   */
  static async navigateToParagraph(paragraphIndex: number): Promise<void> {
    if (!isWordAvailable()) {
      return;
    }

    try {
      await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        if (paragraphIndex >= 0 && paragraphIndex < paragraphs.items.length) {
          const paragraph = paragraphs.items[paragraphIndex];
          
          // Load the paragraph's text property to verify it exists
          paragraph.load('text');
          await context.sync();
          
          const paragraphText = paragraph.text.trim();
          if (!paragraphText) {
            console.warn(`Paragraph at index ${paragraphIndex} is empty`);
            return;
          }
          
          // BEST PRACTICE: Select the paragraph directly
          // This will navigate Word to that location and highlight it
          paragraph.select();
          await context.sync();
          
          console.log(`Navigated to paragraph ${paragraphIndex}`);
        } else {
          console.warn(`Paragraph index ${paragraphIndex} is out of range`);
        }
      });
    } catch (error) {
      logError('Failed to navigate to paragraph', error);
      // Don't throw - just log the error so the UI doesn't break
      console.warn('Paragraph navigation failed, but continuing:', error);
    }
  }

  /** Remove common Word field-code noise (e.g. REF ... MERGEFORMAT); same rules as Ask OOXML path. */
  private static stripWordFieldCodes(text: string): string {
    return TrackChangesParser.stripRefFieldNoise(text || '');
  }

  /** Normalize text for comparison: remove field-codes, collapse whitespace and trim. */
  private static normalizeForMatch(text: string): string {
    return DocumentReader.stripWordFieldCodes(text || '')
      .replace(/^\s*בסעיף\s+[^\-–—]+[-–—]\s*/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Word.js `body.search` rejects strings over 255 characters (`SearchStringInvalidOrTooLong`).
   * Stay under the limit when using the Search API; use paragraph scanning for full long text.
   */
  private static readonly WORD_BODY_SEARCH_MAX_CHARS = 250;

  /**
   * Finds the first body paragraph index whose normalized text matches the search text, or contains it
   * (or a distinctive prefix), using the same heuristics as {@link syncParagraphIndicesFromDocument}.
   * Does not use `body.search`, so there is no 255-character limit on `searchText`.
   */
  static async findParagraphIndexContainingText(searchText: string): Promise<number | null> {
    if (!isWordAvailable() || !searchText || !searchText.trim()) {
      return null;
    }

    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        for (let i = 0; i < paragraphs.items.length; i++) {
          paragraphs.items[i].load('text');
        }
        await context.sync();

        const searchNorm = DocumentReader.normalizeForMatch(searchText);
        if (!searchNorm) {
          return null;
        }

        const normalizedParagraphs: string[] = [];
        for (let i = 0; i < paragraphs.items.length; i++) {
          normalizedParagraphs.push(
            DocumentReader.normalizeForMatch(paragraphs.items[i].text || '')
          );
        }

        for (let i = 0; i < normalizedParagraphs.length; i++) {
          if (normalizedParagraphs[i] === searchNorm) {
            return i;
          }
        }
        if (searchNorm.length >= 40) {
          for (let i = 0; i < normalizedParagraphs.length; i++) {
            const p = normalizedParagraphs[i];
            if (p.includes(searchNorm) || searchNorm.includes(p)) {
              return i;
            }
          }
          const head = searchNorm.slice(0, 80);
          for (let i = 0; i < normalizedParagraphs.length; i++) {
            if (normalizedParagraphs[i].includes(head)) {
              return i;
            }
          }
        }
        return null;
      });
    } catch (error) {
      logError('Failed to find paragraph index containing text', error);
      return null;
    }
  }

  /**
   * Finds the body.paragraphs index whose text matches the given content (normalized).
   * Returns null if not found (e.g. when the paragraph is inside a table).
   */
  static async findParagraphIndexByContent(searchText: string): Promise<number | null> {
    if (!isWordAvailable() || !searchText || !searchText.trim()) {
      return null;
    }

    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const searchNorm = DocumentReader.normalizeForMatch(searchText);

        for (let i = 0; i < paragraphs.items.length; i++) {
          const para = paragraphs.items[i];
          para.load('text');
        }
        await context.sync();

        for (let i = 0; i < paragraphs.items.length; i++) {
          const paraText = paragraphs.items[i].text || '';
          if (DocumentReader.normalizeForMatch(paraText) === searchNorm) {
            return i;
          }
        }
        return null;
      });
    } catch (error) {
      logError('Failed to find paragraph index by content', error);
      return null;
    }
  }

  /**
   * Result of syncing paragraph indices and clause numbers from the document.
   * indices: clause id -> body.paragraphs index
   * clauseNumbers: clause id -> list number string (e.g. "8.4") when the paragraph is in a numbered list
   */
  static async syncParagraphIndicesFromDocument(
    entries: Array<{ id: string; searchText: string }>
  ): Promise<{ indices: Record<string, number>; clauseNumbers: Record<string, string> }> {
    const indices: Record<string, number> = {};
    const clauseNumbers: Record<string, string> = {};
    if (!isWordAvailable() || entries.length === 0) {
      return { indices, clauseNumbers };
    }

    const withText = entries.filter((e) => e.searchText && e.searchText.trim().length > 0);
    if (withText.length === 0) {
      return { indices, clauseNumbers };
    }

    try {
      const { texts: paragraphTexts, listStrings } = await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        for (let i = 0; i < paragraphs.items.length; i++) {
          const para = paragraphs.items[i];
          para.load('text');
          try {
            const listItem = (para as any).listItemOrNullObject;
            if (listItem) {
              listItem.load('listString');
            }
          } catch {
            // listItemOrNullObject / listString not available in this host
          }
        }
        await context.sync();

        const texts: string[] = [];
        const listStringsOut: (string | undefined)[] = [];
        for (let i = 0; i < paragraphs.items.length; i++) {
          const para = paragraphs.items[i];
          texts.push(para.text || '');
          let listString: string | undefined;
          try {
            const li = (para as any).listItemOrNullObject;
            if (li && !li.isNullObject && li.listString != null && String(li.listString).trim() !== '') {
              listString = String(li.listString).trim();
            }
          } catch {
            // ignore
          }
          listStringsOut.push(listString);
        }
        return { texts, listStrings: listStringsOut };
      });

      const normalizedParagraphs = paragraphTexts.map((t) =>
        DocumentReader.normalizeForMatch(t)
      );

      // Fallback: extract leading "8.4" or "8.4." from paragraph text when listString is not set
      const leadingNumberRe = /^\s*(\d+(?:\.\d+)*)[.\s]/;
      for (const { id, searchText } of withText) {
        const searchNorm = DocumentReader.normalizeForMatch(searchText);
        let idx = normalizedParagraphs.findIndex((p) => p === searchNorm);

        if (idx < 0 && searchNorm.length >= 40) {
          idx = normalizedParagraphs.findIndex((p) => p.includes(searchNorm) || searchNorm.includes(p));
        }
        if (idx < 0 && searchNorm.length >= 40) {
          const head = searchNorm.slice(0, 80);
          idx = normalizedParagraphs.findIndex((p) => p.includes(head));
        }

        if (idx >= 0) {
          indices[id] = idx;
          const listStr = listStrings[idx];
          if (listStr) {
            clauseNumbers[id] = listStr;
          } else {
            const text = paragraphTexts[idx] || '';
            const match = text.match(leadingNumberRe);
            if (match && match[1]) {
              clauseNumbers[id] = match[1];
            }
          }
        }
      }

      log('DocumentReader: paragraph sync', {
        matched: Object.keys(indices).length,
        entries: withText.length,
      });

      return { indices, clauseNumbers };
    } catch (error) {
      logError('Failed to sync paragraph indices from document', error);
      return { indices, clauseNumbers };
    }
  }

  /**
   * Navigates to the paragraph that contains the given text by searching the document.
   * Works for content in body paragraphs and inside tables (body.search searches document content).
   * Selects the whole paragraph containing the match.
   *
   * Long strings: Word's Search API allows at most 255 characters — we truncate for `body.search`
   * and fall back to scanning body paragraphs when search fails or finds nothing.
   */
  static async navigateToParagraphByContent(searchText: string): Promise<void> {
    if (!isWordAvailable() || !searchText || !searchText.trim()) {
      return;
    }

    const trimmed = searchText.trim();
    const forSearchApi = trimmed
      .replace(/\s+/g, ' ')
      .slice(0, DocumentReader.WORD_BODY_SEARCH_MAX_CHARS);

    if (!forSearchApi) {
      return;
    }

    let navigatedViaSearch = false;
    try {
      await Word.run(async (context) => {
        const body = context.document.body;
        const searchResults = body.search(forSearchApi, {
          matchCase: false,
          matchWholeWord: false,
        });
        searchResults.load('items');
        await context.sync();

        if (searchResults.items.length === 0) {
          return;
        }

        navigatedViaSearch = true;
        const firstRange = searchResults.items[0];
        const paragraph = firstRange.paragraphs.getFirst();
        paragraph.load('text');
        await context.sync();

        paragraph.select();
        await context.sync();
      });
    } catch (error) {
      logError('Failed to navigate to paragraph by content (search)', error);
      console.warn('Navigate by content: search failed, will try paragraph scan:', error);
    }

    if (navigatedViaSearch) {
      return;
    }

    const idx = await DocumentReader.findParagraphIndexContainingText(trimmed);
    if (idx != null) {
      try {
        await DocumentReader.navigateToParagraph(idx);
      } catch (error) {
        logError('Failed to navigate to paragraph after content scan', error);
      }
    } else {
      console.warn('[DocumentReader] navigateToParagraphByContent: no match found');
    }
  }
}

